import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import DashboardContent from "../../components/dashboard";
import CuVoMetricsDashboardContent from "../../components/cuvo-metrics-dashboard";
import ReportsTicketsDashboardContent from "../../components/reports-tickets-dashboard";
import TicketsMetricsDashboardContent from "../../components/tickets-metrics-dashboard";
import CuVoSuperAdminAccuracyReport from "../../components/cuvo-super-admin-accuracy-report";

import { withSnackbar } from "../../utils/common/SnackbarHoc";
import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import {
  enableChatbot,
  enableCorrelation,
  enableMailReply,
} from "../../utils/config";
import { useLocation, useNavigate } from "react-router-dom";
import { ROLE_USER_SUPERADMIN } from "../../model";

interface TabPanelProps {
  children?: any;
  index: number;
  value: number;
  className: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, className, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className={className}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const Dashboard = () => {
  const { userInfo } = useContext(UserContext) as UserLoginInfo;
  const location = useLocation();
  const navigate = useNavigate();
  let userEnabledFeatures: any = localStorage.getItem("userenabledFeatures");

  if (userEnabledFeatures) {
    userEnabledFeatures = JSON.parse(userEnabledFeatures);
  }
  const tabs = [
    {
      label: "Performance",
      component: <DashboardContent />,
      condition:
        userEnabledFeatures &&
        (userEnabledFeatures.enableFeedback ||
          userEnabledFeatures.enableBugReport ||
          userEnabledFeatures.enableFeatureRequest),
      tabType: "performance",
    },
    {
      label: "Correlation",
      component: <ReportsTicketsDashboardContent />,
      condition: enableCorrelation && userEnabledFeatures?.enableCorrelation,
      tabType: "correlation",
    },
    {
      label: "Chatbot Metrics",
      component: <CuVoMetricsDashboardContent />,
      condition: enableChatbot && userEnabledFeatures?.enableChatbot,
      tabType: "chatbotMetrics",
    },
    {
      label: "Tickets Metrics",
      component: <TicketsMetricsDashboardContent />,
      condition: enableMailReply && userEnabledFeatures?.enableMailReply,
      tabType: "ticketMetrics",
    },
    {
      label: "Accuracy Metrics",
      component: <CuVoSuperAdminAccuracyReport />,
      condition:
        userInfo &&
        userInfo.userDetails &&
        userInfo.userDetails.userRole == ROLE_USER_SUPERADMIN &&
        enableChatbot &&
        userEnabledFeatures?.enableChatbot,
      tabType: "accuracyMetrics",
    },
  ].filter((tab) => tab.condition);

  const initialTabValue = () => {
    if (location && location.state) {
      const tabIndex = tabs.findIndex(
        (e: any) => location.state.pageType === e.tabType
      );
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0; // Default value is 0 if no matching pageType is found
  };

  const [value, setValue] = useState(initialTabValue);

  useEffect(() => {
    if (location && location.state) {
      const tabIndex = tabs.findIndex(
        (e: any) => location.state.pageType === e.tabType
      );
      if (tabIndex !== -1) {
        setValue(tabIndex);
      }
      navigate(location.pathname, { replace: true, state: null });
    }
    // Clear location state after setting the tab value
  }, [location.state, location.pathname, navigate, tabs]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <div className="feedback-wrap">
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleTabChange} aria-label="dashboards">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index} className="">
          {tab.component}
        </TabPanel>
      ))}
    </div>
  );
};

export default withSnackbar(Dashboard);
